import { MouseEvent, useMemo } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import DashboardDataGrid from 'src/common/components/dataGrid/DashboardDataGrid';
import { actionsColumn } from 'src/common/components/dataGrid/dashboardGridColumns';
import { LanguageKeys } from 'src/common/constants/languages';
import { TechnicalType } from 'src/interfaces/TechnicalType';

interface Props {
  items: (TechnicalType & {
    icon?: string;
  })[];
  onDelete: (id: string, e?: MouseEvent<unknown>) => void;
  onEdit: (id: string, e?: MouseEvent<unknown>) => void;
}

const TechnicalTypesTable = ({ onDelete, onEdit, items }: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const columns = useMemo(
    (): GridColDef<TechnicalType>[] => [
      {
        field: 'name',
        headerName: t('technicalTypes.nameLabel'),
        width: 220,
        sortable: false,
      },
      {
        field: 'translatedName',
        headerName: t('technicalTypes.translatedName'),
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => row.i18n[currentLang],
      },
      {
        field: 'published',
        headerName: t('system.sponsors.published'),
        width: 100,
        sortable: false,
        valueGetter: (_, row) => (row.published ? '✓' : '-'),
      },
      actionsColumn<TechnicalType>(t, 'technicalTypes.optionsLabel', ({ row }) => (
        <>
          <Tooltip title={t('technicalTypes.delete')}>
            <IconButton onClick={e => onDelete(row.id, e)} color="error">
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('technicalTypes.edit')}>
            <IconButton size="small" onClick={e => onEdit(row.id, e)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )),
    ],
    [currentLang, onDelete, onEdit, t],
  );

  return (
    <DashboardDataGrid<TechnicalType>
      aria-label={t('technicalTypes.technicalTypesTableLabel')}
      chrome="compact"
      rows={items}
      columns={columns}
    />
  );
};

export default TechnicalTypesTable;
